/**
 * Whether a schedule is actually going to do what its row says.
 *
 * The page it replaces printed a "Next run" for every schedule, including
 * drafts and paused ones whose stored next run had passed weeks ago, so a
 * schedule that would never run read "43d ago" in the column that means "soon".
 * A next run is only shown here when the schedule really will run then; every
 * other state says in words why not.
 *
 * Pure: the clock and the deployment's switches come in as arguments.
 */
import type { ReportSchedule } from '@/pages/pipeline-intelligence/types'
import type { ReportSchedulerStatus } from '@/services/reportSchedules'
import { clockLabel } from '../../pipeline-intelligence/scheduleClock'
import { endSentence, formatAgo, formatWhen, oneLine, parseInstant } from './format'

/**
 * How late an active schedule's next run may be before it counts as overdue.
 *
 * The scheduler sweeps every minute (report_scheduler.CHECK_INTERVAL_SECONDS),
 * so a run that is a few minutes behind is merely queued. Half an hour behind
 * means nothing is picking it up. This value is a judgment, not a contract; it
 * needs the owner's sign-off.
 */
export const OVERDUE_GRACE_MINUTES = 30

export type HealthState =
  | 'healthy'
  | 'first_run_pending'
  | 'event'
  | 'scheduler_off'
  | 'paused'
  | 'draft'
  | 'overdue'
  | 'failed'
  | 'no_next_run'

export type HealthTone = 'ok' | 'warn' | 'bad' | 'muted'

/** Why a schedule is asking for attention. Absent when it is not. */
export type AttentionReason = 'overdue' | 'failed' | 'draft' | 'no_next_run'

export type ScheduleHealth = {
  state: HealthState
  tone: HealthTone
  /** The badge. */
  label: string
  /** One sentence that says what is true and, where it matters, what to do. */
  detail: string
  /** The next run, and only when this schedule really will run then. */
  nextRunAt: string | null
  /** What goes in the "Next run" column. Never a past time presented as upcoming. */
  nextRunText: string
  attention: AttentionReason | null
}

/** Worst first, for ordering a list so the schedules that need a person come first. */
export const HEALTH_SEVERITY: Record<HealthState, number> = {
  overdue: 0,
  failed: 1,
  draft: 2,
  no_next_run: 3,
  scheduler_off: 4,
  paused: 5,
  first_run_pending: 6,
  healthy: 7,
  event: 8,
}

const ZONE_NAMES: Record<string, string> = {
  'America/New_York': 'Eastern',
  'America/Chicago': 'Central',
  'America/Denver': 'Mountain',
  'America/Los_Angeles': 'Pacific',
  'Europe/London': 'London',
  UTC: 'UTC',
}

export function zoneName(timeZone: string | null | undefined): string {
  if (!timeZone) return 'UTC'
  return ZONE_NAMES[timeZone] ?? timeZone
}

const DAY_NAMES = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']

/**
 * How often a schedule fires, in a sentence: "Every weekday at 7:00 AM Eastern".
 * `eventLabel` names the event for an event-triggered schedule.
 */
export function describeCadence(
  schedule: Pick<
    ReportSchedule,
    | 'triggerType'
    | 'triggerEventType'
    | 'frequency'
    | 'dayOfWeek'
    | 'dayOfMonth'
    | 'hourUtc'
    | 'minuteLocal'
    | 'timezone'
    | 'businessDaysOnly'
  >,
  eventLabel?: (type: string) => string,
): string {
  if (schedule.triggerType === 'event') {
    const type = schedule.triggerEventType ?? ''
    return type ? `When ${(eventLabel?.(type) ?? type).toLowerCase()}` : 'On an event'
  }
  const at = `${clockLabel(schedule.hourUtc, schedule.minuteLocal)} ${zoneName(schedule.timezone)}`
  const frequency = String(schedule.frequency ?? '').toLowerCase()
  if (frequency === 'weekly') {
    const day = DAY_NAMES[schedule.dayOfWeek ?? -1]
    return day ? `Every ${day} at ${at}` : `Weekly at ${at}`
  }
  if (frequency === 'biweekly') {
    const day = DAY_NAMES[schedule.dayOfWeek ?? -1]
    return day ? `Every other ${day} at ${at}` : `Every two weeks at ${at}`
  }
  if (frequency === 'monthly') {
    return schedule.dayOfMonth ? `Monthly on day ${schedule.dayOfMonth} at ${at}` : `Monthly at ${at}`
  }
  if (frequency === 'quarterly') {
    return schedule.dayOfMonth ? `Quarterly on day ${schedule.dayOfMonth} at ${at}` : `Quarterly at ${at}`
  }
  if (frequency === 'daily') {
    return schedule.businessDaysOnly ? `Every weekday at ${at}` : `Every day at ${at}`
  }
  return `${frequency ? frequency[0].toUpperCase() + frequency.slice(1) : 'Scheduled'} at ${at}`
}

/** What went wrong, from a `last_run_status` that reads "failed: <reason>". */
export function failureReason(lastRunStatus: string | null | undefined): string | null {
  const status = String(lastRunStatus ?? '')
  if (!/^failed\b/i.test(status)) return null
  const reason = status.replace(/^failed:?\s*/i, '')
  return oneLine(reason, 160) || null
}

export type HealthContext = {
  now?: Date
  /**
   * The deployment's switches. Undefined while they have not loaded (or could
   * not be read): the schedule is then described without a verdict that
   * depends on them.
   */
  status?: Pick<ReportSchedulerStatus, 'generationEnabled'> | undefined
  /** Plain name for an event type, from the scheduler status. */
  eventLabel?: (type: string) => string
}

export function scheduleHealth(schedule: ReportSchedule, context: HealthContext = {}): ScheduleHealth {
  const now = context.now ?? new Date()
  const zone = schedule.timezone || undefined
  const cadence = describeCadence(schedule, context.eventLabel)
  // Every time on a schedule row is read in the schedule's own zone, the one its
  // cadence is written in, so "7:00 AM Eastern" and "Tomorrow 7:00 AM EDT" agree.
  const when = (iso: string | null | undefined) => formatWhen(iso, { now, timeZone: zone, withZone: true })
  const lastRan = schedule.lastRunAt ? when(schedule.lastRunAt) : null

  const make = (
    state: HealthState,
    tone: HealthTone,
    label: string,
    detail: string,
    extra: Partial<Pick<ScheduleHealth, 'nextRunAt' | 'nextRunText' | 'attention'>> = {},
  ): ScheduleHealth => ({
    state,
    tone,
    label,
    detail,
    nextRunAt: extra.nextRunAt ?? null,
    nextRunText: extra.nextRunText ?? '—',
    attention: extra.attention ?? null,
  })

  if (!schedule.isActive) {
    if (!schedule.lastRunAt) {
      return make(
        'draft',
        'warn',
        'Draft',
        'Saved but never run. It starts only after someone with approval activates it.',
        { nextRunText: 'Not scheduled (draft)', attention: 'draft' },
      )
    }
    const stored = parseInstant(schedule.nextRunAt)
    const passed =
      stored && !Number.isNaN(stored.valueOf()) && stored.valueOf() < now.valueOf()
        ? ` The run time stored on it (${formatAgo(stored, now)}) has passed; activating it sets a new one.`
        : ''
    return make('paused', 'muted', 'Paused', `Paused. Last ran ${lastRan}.${passed}`, {
      nextRunText: 'Not scheduled (paused)',
    })
  }

  if (schedule.triggerType === 'event') {
    const failed = failureReason(schedule.lastRunStatus)
    if (failed !== null || /^failed/i.test(schedule.lastRunStatus ?? '')) {
      return make('failed', 'bad', 'Last run failed', endSentence(`Last run failed: ${failed ?? 'no reason recorded'}`), {
        nextRunText: 'On event',
        attention: 'failed',
      })
    }
    return make('event', 'ok', 'Active', `${cadence}.`, { nextRunText: 'On event' })
  }

  if (context.status && !context.status.generationEnabled) {
    return make(
      'scheduler_off',
      'warn',
      'Scheduler off',
      'Active, but scheduled generation is off on this deployment, so it will not run on its own.',
      { nextRunText: 'Not running (scheduler off)' },
    )
  }

  const nextValid = parseInstant(schedule.nextRunAt)

  if (!nextValid) {
    return make(
      'no_next_run',
      'bad',
      'No next run',
      'Active, but no next run is set, so nothing will fire. Pause and activate it to set one.',
      { nextRunText: 'None set', attention: 'no_next_run' },
    )
  }

  const lateMinutes = (now.valueOf() - nextValid.valueOf()) / 60_000
  if (context.status && lateMinutes > OVERDUE_GRACE_MINUTES) {
    return make(
      'overdue',
      'bad',
      'Overdue',
      `Was due ${when(schedule.nextRunAt)} and has not run.`,
      { nextRunText: `Overdue (${formatAgo(nextValid, now).replace(' ago', '')})`, attention: 'overdue' },
    )
  }

  const failed = failureReason(schedule.lastRunStatus)
  if (failed !== null || /^failed/i.test(schedule.lastRunStatus ?? '')) {
    return make('failed', 'bad', 'Last run failed', endSentence(`Last run failed: ${failed ?? 'no reason recorded'}`), {
      nextRunAt: schedule.nextRunAt ?? null,
      nextRunText: when(schedule.nextRunAt),
      attention: 'failed',
    })
  }

  if (!schedule.lastRunAt) {
    return make('first_run_pending', 'ok', 'Active', `${cadence}. First run ${when(schedule.nextRunAt)}.`, {
      nextRunAt: schedule.nextRunAt ?? null,
      nextRunText: when(schedule.nextRunAt),
    })
  }

  return make('healthy', 'ok', 'Active', `${cadence}. Last ran ${lastRan}.`, {
    nextRunAt: schedule.nextRunAt ?? null,
    nextRunText: when(schedule.nextRunAt),
  })
}
